/**
 * What a Kho bài row is allowed to say about a unit. GET /izt-content/tree carries mediaKind,
 * sizeBytes and durationSeconds per unit; null means the record does not hold that fact, so the
 * row drops the part instead of guessing one. Page count is deliberately absent - it exists
 * nowhere in the database and the board derives it from the rendered PDF.
 */

import type { IztContentNode } from '../../api/types';
import { contentKind, contentKindLabel, contentMetaLine } from '../../config/content-types.ts';
import { formatBytes } from '../../config/file-size.ts';

export type ContentUnit = IztContentNode['sections'][number]['units'][number];

/** Real question count when the tree supplies one; an uncountable payload counts as unknown. */
export function unitQuestionCount(value: unknown): number | null {
  if (typeof value === 'number' && isFinite(value) && value > 0) return Math.round(value);
  if (Array.isArray(value) && value.length > 0) return value.length;
  return null;
}

/** "22:48", or "1:02:18" past the hour. Empty when the resource carries no metadata.duration. */
export function formatDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mm = hours > 0 && minutes < 10 ? `0${minutes}` : String(minutes);
  const ss = secs < 10 ? `0${secs}` : String(secs);
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** mediaKind names the two link cases the unit type cannot express; otherwise the type word wins. */
export function unitMediaLabel(unit: ContentUnit): string {
  if (unit.mediaKind === 'youtube') return 'Video YouTube';
  if (unit.mediaKind === 'external') return 'Liên kết ngoài';
  if (unit.mediaKind === 'video') return 'Video';
  return contentKindLabel(unit.type);
}

/** The facts without the leading type word, for surfaces that print that word themselves. */
export function unitFactLine(unit: ContentUnit): string {
  if (contentKind(unit.type) === 'assessment') {
    const count = unitQuestionCount(unit.homeworkQuestion);
    return count ? `${count} câu hỏi` : '';
  }
  return contentMetaLine([formatDuration(unit.durationSeconds), formatBytes(unit.sizeBytes)]);
}

/** "Video • 22:48 • 75 MB" - a unit the server knows nothing about is left as its type word alone. */
export function unitSubtitle(unit: ContentUnit): string {
  return contentMetaLine([unitMediaLabel(unit), unitFactLine(unit)]);
}

export interface CourseTotals {
  chapters: number;
  units: number;
}

/** Counted from the tree - the only course-level facts that exist anywhere in the payload. */
export function courseTotals(course: { sections: Array<{ units: unknown[] }> }): CourseTotals {
  let units = 0;
  course.sections.forEach((section) => { units += section.units.length; });
  return { chapters: course.sections.length, units };
}
